import { TFunction } from 'i18next';
import { currencyFormatter } from '../../../services/helperService';
import { getSubCategoryDisplayName } from '../../orderhistory/categoryUtils';
import {
  CountReviewProduct,
  CountReviewProductMinimum,
  CountReviewResponse,
} from '../../../services/inventory/inventoryService';
import { baseProductKey } from '../../../utils/inventoryProductKey';
import { buildCategoryBreakdown } from './ValueByCategoryCard';
import { productCategoryLabel, frozenStdUnitPrice, fmtQuantity } from './inventoryReviewShared';
import { groupBelowMinBySupplier, ORDER_LIST_OTHER_KEY } from './inventoryOrderList';

/**
 * Client-side .txt export of the Count Review view (markdown-formatted, mirroring the
 * super-admin data export style: `##` sections + pipe tables). Pure — builds the file
 * content from the already-loaded CountReviewResponse, no API calls.
 */

export interface CountReviewExportContext {
  restaurantName: string;
  /** Group filter label: "All inventories" or the group name. */
  groupLabel: string;
  /** Count selector label: "Latest" or "{group} · {date}". */
  sessionLabel: string;
  /** Date of the explicit "Compared against" count; null/absent = the automatic previous count. */
  baselineLabel?: string | null;
  generatedAt: Date;
}

const eur = (n: number | null | undefined): string =>
  n == null || !Number.isFinite(n) ? 'n/a' : currencyFormatter.format(n);

const pct = (share: number): string => `${(share * 100).toFixed(1)}%`;

/** Signed change amount ("+€12.00" / "−€3.50"), or 'n/a' when no previous count exists. */
const change = (n: number | null): string =>
  n == null ? 'n/a' : `${n >= 0 ? '+' : '−'}${currencyFormatter.format(Math.abs(n))}`;

function mdTable(t: TFunction, headers: string[], rows: (string | number)[][]): string {
  if (rows.length === 0) return `_${t('inventory.exportNoData')}_`;
  const head = `| ${headers.join(' | ')} |`;
  const sep = `| ${headers.map(() => '---').join(' | ')} |`;
  const body = rows.map((r) => `| ${r.map((c) => String(c)).join(' | ')} |`).join('\n');
  return [head, sep, body].join('\n');
}

/** Supplier as text: platform id (capitalised) when set, else the supplier name. */
export function supplierText(supplier: { platform: string | null; name: string | null }): string {
  const raw = supplier.platform ?? supplier.name;
  if (!raw) return '—';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

/**
 * Where the row's product comes from: an order-history product, a custom ingredient
 * (something the restaurant buys but never through a tracked invoice) or a subrecipe
 * counted as a prep.
 */
export function sourceTypeText(t: TFunction, p: CountReviewProduct): string {
  if (p.source === 'custom') return t('inventory.exportTypeNeverOrdered');
  if (p.source === 'subrecipe') return t('inventory.exportTypeFromSubrecipe');
  return t('inventory.exportTypeFromOrders');
}

export function statusText(t: TFunction, p: CountReviewProduct): string {
  const flags: string[] = [];
  if (!p.linked) flags.push(t('inventory.badgeNotLinked'));
  if (p.missingPrice) flags.push(t('inventory.badgeNoPrice'));
  if (p.belowMinimum) flags.push(t('inventory.badgeBelowMin'));
  return flags.length ? flags.join(', ') : '—';
}

/**
 * Product minimums indexed by BASE product key — variant rows (`…__v2`) share the one
 * minimum of the physical product, exactly like the below-minimum pass does.
 */
export function buildMinimumLookup(data: CountReviewResponse): Map<string, CountReviewProductMinimum> {
  return new Map((data.productMinimums ?? []).map((m) => [m.productKey, m]));
}

/**
 * "Minimum threshold" cell: the product's minimum WITH its unit, or a dash when it has
 * none. The value is a restaurant-wide total across sections, so the same figure repeats
 * on each section a product is counted in.
 */
export function minimumText(m: CountReviewProductMinimum | undefined): string {
  return m ? `${fmtQuantity(m.minimum)} ${m.countUnit}` : '—';
}

/** "Order amount needed" cell: the shortfall with its unit, `0` when stocked, dash without a minimum. */
export function toOrderText(m: CountReviewProductMinimum | undefined): string {
  if (!m) return '—';
  return m.toOrder > 0 ? `${fmtQuantity(m.toOrder)} ${m.countUnit}` : '0';
}

function unitPriceText(p: CountReviewProduct): string {
  const std = frozenStdUnitPrice(p);
  return std ? `${currencyFormatter.format(std.value)} / ${std.unit}` : '—';
}

const countText = (p: CountReviewProduct): string => `${fmtQuantity(p.count)} ${p.countUnit}`;
const quantityText = (p: CountReviewProduct): string =>
  `${fmtQuantity(p.quantityDisplay.value)} ${p.quantityDisplay.unit}`;

/** Category tables shared by the top-level breakdown and each section detail. */
function inventoryCategoryTable(
  t: TFunction,
  rows: CountReviewResponse['categoriesInventory']
): string {
  return mdTable(
    t,
    [t('inventory.colInvCategory'), t('inventory.value'), t('inventory.exportColShare'), t('inventory.exportColProducts'), t('inventory.exportColBelowMin')],
    rows.map((c) => [c.name || t('inventory.uncategorizedBucket'), eur(c.totalValue), pct(c.share), c.productCount, c.belowMinCount])
  );
}

function productCategoryTable(
  t: TFunction,
  rows: CountReviewResponse['categoriesProduct']
): string {
  const tableRows: (string | number)[][] = [];
  for (const c of rows) {
    tableRows.push([
      productCategoryLabel(t, c.key, c.mainCategory),
      eur(c.totalValue),
      pct(c.share),
      c.productCount,
      c.belowMinCount,
    ]);
    // Subcategory rows indented under their main category; skip when the only
    // bucket is the "no subcategory" one (no extra information).
    const subs = c.subCategories.filter((s) => s.key !== null || c.subCategories.length > 1);
    for (const s of subs) {
      const label = s.key ? getSubCategoryDisplayName(s.key) ?? s.key : t('inventory.noSubcategoryBucket');
      tableRows.push([`↳ ${label}`, eur(s.totalValue), pct(s.share), s.productCount, s.belowMinCount]);
    }
  }
  return mdTable(
    t,
    [t('inventory.colProductCategory'), t('inventory.value'), t('inventory.exportColShare'), t('inventory.exportColProducts'), t('inventory.exportColBelowMin')],
    tableRows
  );
}

/** Compact attention-list table (not linked / missing price). */
function attentionTable(t: TFunction, products: CountReviewProduct[]): string {
  return mdTable(
    t,
    [t('inventory.product'), t('inventory.exportColSection'), t('inventory.colSupplier'), t('inventory.count'), t('inventory.value')],
    products
      .slice()
      .sort((a, b) => b.value - a.value)
      .map((p) => [p.productName, p.sectionName, supplierText(p.supplier), countText(p), eur(p.value)])
  );
}

/**
 * Full product-rows table (everything the section detail page shows, incl. expanded columns)
 * plus the minimum threshold / order-amount-needed pair. Those two are product TOTALS across
 * sections (the minimum is restaurant-wide), so they repeat identically on every section a
 * product appears in.
 */
function productsTable(
  t: TFunction,
  products: CountReviewProduct[],
  minimums: Map<string, CountReviewProductMinimum>
): string {
  return mdTable(
    t,
    [
      t('inventory.product'),
      t('inventory.exportColType'),
      t('inventory.colSupplier'),
      t('inventory.colInvCategory'),
      t('inventory.colProductCategory'),
      t('inventory.count'),
      t('inventory.colQuantity'),
      t('inventory.exportColMinThreshold'),
      t('inventory.exportColOrderNeeded'),
      t('inventory.unitPrice'),
      t('inventory.value'),
      t('inventory.colStatus'),
    ],
    products
      .slice()
      .sort((a, b) => b.value - a.value)
      .map((p) => {
        const min = minimums.get(baseProductKey(p.productKey));
        return [
          p.productName,
          sourceTypeText(t, p),
          supplierText(p.supplier),
          p.inventoryCategoryName ?? t('inventory.uncategorizedBucket'),
          productCategoryLabel(t, p.productCategory, p.mainCategory),
          countText(p),
          quantityText(p),
          minimumText(min),
          toOrderText(min),
          unitPriceText(p),
          eur(p.value),
          statusText(t, p),
        ];
      })
  );
}

/** Below-minimum products with their threshold and needed order (group-level totals). */
function belowMinimumTable(t: TFunction, data: CountReviewResponse): string {
  return mdTable(
    t,
    [
      t('inventory.product'),
      t('inventory.exportColSection'),
      t('inventory.colSupplier'),
      t('inventory.count'),
      t('inventory.colThreshold'),
      t('inventory.colNeededOrder'),
      t('inventory.colOrderUnits'),
      t('inventory.value'),
    ],
    data.belowMinProducts.map((p) => [
      p.productName,
      p.sections.join(', '),
      supplierText(p.supplier),
      `${fmtQuantity(p.currentCount)} ${p.countUnit}`,
      p.minimum != null ? `${fmtQuantity(p.minimum)} ${p.countUnit}` : 'n/a',
      p.toOrder != null ? `${fmtQuantity(p.toOrder)} ${p.countUnit}` : 'n/a',
      p.toOrderUnits != null ? fmtQuantity(p.toOrderUnits) : 'n/a',
      eur(p.value),
    ])
  );
}

/** The order list: below-minimum products grouped per supplier (same grouping as the drawer). */
function orderListSection(t: TFunction, data: CountReviewResponse): string[] {
  const parts: string[] = [];
  const groups = groupBelowMinBySupplier(data.belowMinProducts);
  if (groups.length === 0) {
    parts.push(`_${t('inventory.orderListEmpty')}_`);
    return parts;
  }
  for (const g of groups) {
    parts.push(`### ${g.key === ORDER_LIST_OTHER_KEY ? t('inventory.orderListOtherSupplier') : g.label} (${g.products.length})`);
    parts.push(
      mdTable(
        t,
        [
          t('inventory.product'),
          t('inventory.count'),
          t('inventory.colThreshold'),
          t('inventory.colNeededOrder'),
          t('inventory.colOrderUnits'),
        ],
        g.products.map((p) => [
          p.productName,
          `${fmtQuantity(p.currentCount)} ${p.countUnit}`,
          p.minimum != null ? `${fmtQuantity(p.minimum)} ${p.countUnit}` : 'n/a',
          p.toOrder != null ? `${fmtQuantity(p.toOrder)} ${p.countUnit}` : 'n/a',
          p.toOrderUnits != null ? fmtQuantity(p.toOrderUnits) : 'n/a',
        ])
      )
    );
    parts.push('');
  }
  return parts;
}

export function buildCountReviewExport(
  t: TFunction,
  data: CountReviewResponse,
  ctx: CountReviewExportContext
): string {
  const { kpis } = data;
  const parts: string[] = [];
  // An explicit "Compared against" count is named, so a custom comparison never reads as the default.
  const changeCol = ctx.baselineLabel
    ? t('inventory.exportColChangeVsDate', { date: ctx.baselineLabel })
    : t('inventory.exportColChange');

  // Header
  parts.push(`# ${t('inventory.exportTitle')} — ${ctx.restaurantName}`);
  parts.push('');
  parts.push(`- ${t('inventory.exportGroup')}: ${ctx.groupLabel}`);
  parts.push(`- ${t('inventory.exportCount')}: ${ctx.sessionLabel}`);
  if (ctx.baselineLabel) parts.push(`- ${t('inventory.leakage.comparedAgainst')}: ${ctx.baselineLabel}`);
  parts.push(`- ${t('inventory.exportGenerated')}: ${ctx.generatedAt.toLocaleString()}`);
  parts.push(`- ${t('inventory.kpiTotalCountedValue')}: ${eur(kpis.totalValue)} (${changeCol}: ${change(kpis.changeValue)})`);
  parts.push(`- ${t('inventory.kpiProductsCounted')}: ${kpis.productsCount} · ${t('inventory.kpiProductsNotLinked')}: ${kpis.notLinkedCount} · ${t('inventory.kpiNeedsOrdering')}: ${kpis.needsOrderingCount}`);
  parts.push('');

  // 1. Value by section (plus the by-inventory table on the all-groups view)
  parts.push(`## 1. ${t('inventory.valueBySection')}`);
  parts.push('');
  if (data.selection.groupId === null && data.groups.length > 0) {
    parts.push(`### ${t('inventory.valueByInventory')}`);
    parts.push(
      mdTable(
        t,
        [t('inventory.exportColInventory'), t('inventory.value'), t('inventory.exportColShare'), t('inventory.exportColProducts'), t('inventory.exportColBelowMin'), changeCol],
        data.groups.map((g) => [
          g.groupName ?? t('inventory.ungrouped'),
          eur(g.totalValue),
          pct(g.share),
          g.productCount,
          g.belowMinCount,
          change(g.changeValue),
        ])
      )
    );
    parts.push('');
    parts.push(`### ${t('inventory.valueBySection')}`);
  }
  parts.push(
    mdTable(
      t,
      [t('inventory.exportColSection'), t('inventory.exportColInventory'), t('inventory.value'), t('inventory.exportColShare'), t('inventory.exportColProducts'), t('inventory.exportColBelowMin'), changeCol],
      data.sections.map((s) => [
        s.sectionName,
        s.groupName ?? t('inventory.ungrouped'),
        eur(s.totalValue),
        pct(s.share),
        s.productCount,
        s.belowMinCount,
        change(s.changeValue),
      ])
    )
  );
  parts.push('');

  // 2. Value by inventory categories
  parts.push(`## 2. ${t('inventory.catModeInventory')}`);
  parts.push('');
  parts.push(inventoryCategoryTable(t, data.categoriesInventory));
  parts.push('');

  // 3. Value by product categories
  parts.push(`## 3. ${t('inventory.catModeProduct')}`);
  parts.push('');
  parts.push(productCategoryTable(t, data.categoriesProduct));
  parts.push('');

  // 4. Products needing attention
  const notLinked = data.products.filter((p) => !p.linked);
  const missingPrice = data.products.filter((p) => p.missingPrice);
  parts.push(`## 4. ${t('inventory.attentionTitle')}`);
  parts.push('');
  parts.push(`### A. ${t('inventory.attNotLinked')} (${notLinked.length})`);
  parts.push(attentionTable(t, notLinked));
  parts.push('');
  parts.push(`### B. ${t('inventory.attMissingPrice')} (${missingPrice.length})`);
  parts.push(attentionTable(t, missingPrice));
  parts.push('');
  parts.push(`### C. ${t('inventory.attBelowMin')} (${data.belowMinProducts.length})`);
  parts.push(belowMinimumTable(t, data));
  parts.push('');

  // 5. Order list — the same below-minimum products, grouped per supplier so the list
  // can be worked through supplier by supplier when ordering.
  parts.push(`## 5. ${t('inventory.orderListTitle')}`);
  parts.push('');
  parts.push(...orderListSection(t, data));

  // 6. Per-section details (the section detail page: category breakdowns + all product rows)
  const minimums = buildMinimumLookup(data);
  parts.push(`## 6. ${t('inventory.exportSectionDetails')}`);
  parts.push('');
  data.sections.forEach((s, i) => {
    // Match by id; deleted sections (null id) fall back to the snapshot name.
    const sectionProducts = data.products.filter((p) =>
      s.sectionId !== null ? p.sectionId === s.sectionId : p.sectionId === null && p.sectionName === s.sectionName
    );
    const breakdown = buildCategoryBreakdown(sectionProducts);
    parts.push(`### 6.${i + 1} ${s.sectionName} (${s.groupName ?? t('inventory.ungrouped')})`);
    parts.push('');
    parts.push(
      `- ${t('inventory.value')}: ${eur(s.totalValue)} · ${t('inventory.exportColShare')}: ${pct(s.share)} · ${t('inventory.exportColProducts')}: ${s.productCount} · ${t('inventory.exportColBelowMin')}: ${s.belowMinCount} · ${changeCol}: ${change(s.changeValue)}`
    );
    parts.push('');
    parts.push(`#### ${t('inventory.catModeInventory')}`);
    parts.push(inventoryCategoryTable(t, breakdown.inventory));
    parts.push('');
    parts.push(`#### ${t('inventory.catModeProduct')}`);
    parts.push(productCategoryTable(t, breakdown.product));
    parts.push('');
    parts.push(`#### ${t('inventory.productsInSection')} (${sectionProducts.length})`);
    parts.push(productsTable(t, sectionProducts, minimums));
    parts.push('');
  });

  return parts.join('\n');
}

/** Filename: inventory-count-<restaurant-slug>-<yyyy-mm-dd>.txt */
export function countReviewExportFilename(restaurantName: string, generatedAt: Date): string {
  const slug = restaurantName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  const y = generatedAt.getFullYear();
  const m = String(generatedAt.getMonth() + 1).padStart(2, '0');
  const d = String(generatedAt.getDate()).padStart(2, '0');
  return `inventory-count-${slug || 'export'}-${y}-${m}-${d}.txt`;
}
